import { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { NotificationDialog } from '../components'

const NavigationGuardContext = createContext(null)

export function NavigationGuardProvider({ children }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [submissionStatus, setSubmissionStatus] = useState('none')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const location = useLocation()
  const navigate = useNavigate()
  const navigationAttemptRef = useRef(false)

  // Use ref instead of state for callbacks to avoid unnecessary re-renders
  const callbacksRef = useRef({ onSave: null, onDiscard: null })

  // Track location changes - reset state when navigation completes
  useEffect(() => {
    // Only reset if there was an actual navigation attempt
    if (navigationAttemptRef.current) {
      navigationAttemptRef.current = false
      setPendingNavigation(null)
      setDialogOpen(false)
      setSubmissionStatus('none')
    }
  }, [location])

  // Browser-level protection
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const registerUnsavedChanges = useCallback((saveCallback, discardCallback) => {
    setHasUnsavedChanges(true)
    callbacksRef.current = { onSave: saveCallback, onDiscard: discardCallback }
  }, [])

  const unregisterUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false)
    callbacksRef.current = { onSave: null, onDiscard: null }
    // Only reset submission status if not in the middle of a save flow
    setSubmissionStatus((current) =>
      current === 'saving' || current === 'success' || current === 'fail' ? current : 'none'
    )
  }, [])

  const continueNavigation = useCallback(() => {
    setDialogOpen(false)
    if (pendingNavigation) {
      setHasUnsavedChanges(false)
      callbacksRef.current = { onSave: null, onDiscard: null }
      setSubmissionStatus('none')
      navigate(pendingNavigation)
      setPendingNavigation(null)
    }
    navigationAttemptRef.current = false
  }, [pendingNavigation, navigate])

  const cancelNavigation = useCallback(() => {
    setDialogOpen(false)
    setPendingNavigation(null)
    setSubmissionStatus('none')
    navigationAttemptRef.current = false
  }, [])

  const handleSaveChanges = useCallback(() => {
    if (callbacksRef.current.onSave) {
      setDialogOpen(false)
      setSubmissionStatus('saving')
      callbacksRef.current.onSave()
    }
  }, [])

  const handleDiscardChanges = useCallback(() => {
    if (callbacksRef.current.onDiscard) {
      callbacksRef.current.onDiscard()
    }
    continueNavigation()
  }, [continueNavigation])

  const onSubmitSuccess = useCallback(() => {
    // Only show success dialog if there's a pending navigation
    if (pendingNavigation) {
      setSubmissionStatus('success')
      setDialogOpen(true)
    }
  }, [pendingNavigation])

  const onSubmitFail = useCallback(() => {
    // Only show fail dialog if there's a pending navigation
    if (pendingNavigation) {
      setSubmissionStatus('fail')
      setDialogOpen(true)
    }
  }, [pendingNavigation])

  // Dialog configuration based on submission status (memoized for performance)
  const dialogConfig = useMemo(() => {
    switch (submissionStatus) {
      case 'success':
        return {
          type: 'success',
          title: 'Success!',
          message: 'Your changes were applied to your profile.',
          actions: [{ label: 'Done', onClick: continueNavigation }],
        }
      case 'fail':
        return {
          type: 'error',
          title: 'Something went wrong!',
          message: "Your changes couldn't be applied to your profile.",
          actions: [{ label: 'Done', onClick: cancelNavigation }],
        }
      case 'none':
      default:
        return {
          type: 'alert',
          title: 'Wait! You have unsaved changes',
          message: 'Do you want to apply these changes to your profile?',
          actions: [
            { label: 'Save Changes', onClick: handleSaveChanges },
            { label: 'Discard Changes', onClick: handleDiscardChanges },
          ],
        }
    }
  }, [submissionStatus, continueNavigation, cancelNavigation, handleSaveChanges, handleDiscardChanges])

  const checkNavigation = useCallback(
    (to) => {
      if (hasUnsavedChanges) {
        navigationAttemptRef.current = true
        setPendingNavigation(to)
        setDialogOpen(true)
        return false
      }
      return true
    },
    [hasUnsavedChanges]
  )

  const value = useMemo(
    () => ({
      hasUnsavedChanges,
      registerUnsavedChanges,
      unregisterUnsavedChanges,
      onSubmitSuccess,
      onSubmitFail,
      checkNavigation,
    }),
    [
      hasUnsavedChanges,
      registerUnsavedChanges,
      unregisterUnsavedChanges,
      onSubmitSuccess,
      onSubmitFail,
      checkNavigation,
    ]
  )

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <NotificationDialog
        open={dialogOpen}
        onClose={cancelNavigation}
        type={dialogConfig.type}
        title={dialogConfig.title}
        message={dialogConfig.message}
        actions={dialogConfig.actions}
      />
    </NavigationGuardContext.Provider>
  )
}

export function useNavigationGuard() {
  const context = useContext(NavigationGuardContext)
  if (!context) {
    throw new Error('useNavigationGuard must be used within NavigationGuardProvider')
  }
  return context
}
